import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/molecules/card';
import { AsyncCombobox } from '../../../components/ui/molecules/async-combobox';
import { sortByLabel } from '../../../lib/utils';
import { ChartCardProps, LegendItem } from './interface';

/** Generic segmented control, read as a radio group — one option selected, the rest plain. */
export function SegmentedToggle<T extends string>({
  value, options, onChange, label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  /** Accessible name of the group. */
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex rounded-md border border-input overflow-hidden text-sm">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={`px-3 py-2 transition-colors whitespace-nowrap ${
            value === o.value ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Checklist in a popover. An empty selection means "all" rather than "none" —
 * showing nothing when the user hasn't chosen yet would look broken, and it
 * matches how the backend treats an absent filter.
 */
export function MultiSelect({
  label, options, selected, onChange, disabled, emptyHint,
}: {
  label: string;
  /** `hint` explains a disabled option. A disabled option keeps its tick but is left out of the results. */
  options: { id: string; name: string; disabled?: boolean; hint?: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  emptyHint?: string;
}) {
  const [search, setSearch] = useState('');
  const sortedOptions = useMemo(
    () => sortByLabel(options, (o) => o.name).map((o) => ({
      value: o.id, label: o.name, disabled: o.disabled, description: o.disabled ? o.hint : undefined,
    })),
    [options],
  );
  const query = search.trim().toLowerCase();
  const visibleOptions = query ? sortedOptions.filter((o) => o.label.toLowerCase().includes(query)) : sortedOptions;
  // `selected` is the literal list of chosen ids. It deliberately does NOT use
  // "empty means all" — that encoding makes "Deselect all" unrepresentable and
  // turns a click on one row into "select only this one".
  const allSelected = options.length > 0 && selected.length === options.length;

  const toggleAll = () => onChange(allSelected ? [] : options.map((o) => o.id));

  const summary = allSelected
    ? `All ${label.toLowerCase()}`
    : selected.length === 0
      ? `No ${label.toLowerCase()}`
      : undefined;

  return (
    <AsyncCombobox
      multiple
      value={selected}
      options={visibleOptions}
      selectedOptions={sortedOptions}
      search={search}
      onSearchChange={setSearch}
      onValueChange={onChange}
      displayValue={summary}
      header={(
        <div className="flex items-center justify-between px-2 pb-1 pt-2">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <button type="button" onClick={toggleAll} className="text-xs text-primary hover:underline">
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
      )}
      searchPlaceholder={`Search ${label.toLowerCase()}...`}
      emptyMessage={options.length === 0 ? emptyHint ?? 'Nothing to show.' : `No matching ${label.toLowerCase()}.`}
      disabled={disabled}
      className="h-9 w-52"
    />
  );
}

export function Loading() {
  return <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>;
}

export function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground py-8 text-center">{message}</p>;
}

/** A chart with its title, a Chart / Table switch, and shared loading, error and empty states. */
export function ChartCard({
  title, subtitle, isLoading, isError, isEmpty, emptyMessage, chart, table,
}: ChartCardProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const ready = !isLoading && !isError && !isEmpty;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {ready && (
          <SegmentedToggle
            label={`${title} view`}
            value={view}
            options={[{ value: 'chart', label: 'Chart' }, { value: 'table', label: 'Table' }]}
            onChange={setView}
          />
        )}
      </CardHeader>
      <CardContent>
        {isError ? (
          <p className="py-8 text-center text-sm text-destructive">Couldn&apos;t load this chart. Try changing a filter or reloading.</p>
        ) : isLoading ? (
          <div className="h-64 animate-pulse rounded-md bg-muted/40" aria-busy="true" />
        ) : isEmpty ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : view === 'chart' ? chart : table}
      </CardContent>
    </Card>
  );
}

/** Colored swatch plus text. Identity comes from the swatch; the text stays in the text color. */
export function ChartLegend({ items }: { items: LegendItem[] }) {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: item.color }} />
          <span className="text-foreground">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}
